import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from 'redux/baseQuery';
import {
  ProfileTypes,
  TabsTypes,
  ResponseGetTabType,
  GetTabResponse,
  GetTabArg,
  ResponseGetReportsType,
  GetReportsArg,
  BodySignContact,
  ResponsePromoCodeType,
  BodyPromoCode,
  SmsCodeResponse,
  UserInfoResponse,
  DevicesResponseTypes,
  TelegramTokenTypes,
  TelegramSettingsTypes,
  TelegramNotificationsBody,
} from './type';

const getToken = () => localStorage.getItem('token');

const getAuthorizationHeader = () => {
  const token = getToken();
  return token ? `Bearer ${token}` : '';
};

export const profileApi = createApi({
  reducerPath: 'profileApi',
  tagTypes: ['Profile', 'Tabs', 'Tab', 'UserInfo'],
  refetchOnReconnect: true,
  keepUnusedDataFor: 15,
  baseQuery: baseQueryWithReauth(),
  endpoints: (build) => ({
    getProfile: build.query<ProfileTypes, void>({
      query: () => ({
        url: `/customer`,
        method: 'GET',
      }),
      providesTags: () => ['Profile'],
      transformResponse: (response: ProfileTypes) => {
        try {
          localStorage.setItem(
            'profile_settings',
            JSON.stringify({
              use_fast_withdraw: response?.settings?.use_fast_withdraw || false,
            }),
          );
        } catch (e) {
          console.error('Ошибка при сохранении настроек', e);
        }
        return response;
      },
      transformErrorResponse: (response: unknown) => {
        console.error('Ошибка загрузки профиля', response);
        return response;
      },
      extraOptions: {
        maxRetries: 5,
      },
    }),
    getTabs: build.query<TabsTypes[], void>({
      query: () => ({
        url: `/customer/tabs`,
        method: 'GET',
      }),
      providesTags: () => ['Tabs'],
      transformResponse: (response: ResponseGetTabType) => response?.items,
    }),
    getTab: build.query<GetTabResponse, GetTabArg>({
      query: ({ title, params = {} }) => ({
        url: `/customer/tabs/${title}`,
        method: 'GET',
        params,
      }),
      providesTags: () => ['Tab'],
      serializeQueryArgs: ({ endpointName, queryArgs: { title, params } }) =>
        `${endpointName}(${title}:${params?.year}:${params?.month})`,
      merge: (currentCache, newItems, { arg }) => {
        const currentPage = arg?.params?.page ?? 1;

        if (currentPage === 1 || !currentCache?.data?.length || !newItems.data?.length) {
          return newItems;
        }

        return {
          ...newItems,
          amount_by_payment_types: currentCache.amount_by_payment_types,
          total_amount: currentCache.total_amount,
          count: newItems.count ?? currentCache.count,
          data: [...currentCache.data, ...newItems.data],
        };
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    getReports: build.query<ResponseGetReportsType, GetReportsArg>({
      query: () => ({
        url: `/customer/docs/monthly-reports`,
        method: 'GET',
      }),
      providesTags: () => ['Tabs'],
    }),
    signContact: build.mutation<boolean, BodySignContact>({
      query: ({ body }) => ({
        url: `/customer/docs/oferta/sign`,
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Profile'],
    }),
    promoCode: build.mutation<ResponsePromoCodeType, BodyPromoCode>({
      query: ({ body }) => ({
        url: `/customer/promo-code/redeem`,
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Profile'],
    }),
    getSmsCode: build.query<SmsCodeResponse, void>({
      query: () => ({
        url: '/customer/docs/oferta/get-sms-code',
        method: 'GET',
      }),
    }),
    getInfoUser: build.query<UserInfoResponse, void>({
      keepUnusedDataFor: 0,
      query: () => ({
        url: 'user',
        method: 'GET',
      }),
      providesTags: () => ['UserInfo'],

      transformErrorResponse: (response: unknown) => {
        console.error('Ошибка загрузки пользователя:', response);
        return response;
      },
      extraOptions: {
        maxRetries: 5,
      },
    }),
    changePassword: build.mutation<
      { message: string },
      { current_password: string; password: string; password_confirmation: string }
    >({
      query: (body) => ({
        url: '/auth/change-password',
        method: 'POST',
        body,
      }),
      extraOptions: {
        maxRetries: 0,
      },
      transformErrorResponse: (response: {
        status: number;
        data: { message: string };
      }) => {
        return response.data?.message || 'Ошибка при смене пароля';
      },
    }),
    // getLogs: build.query<any, void>({
    //   query: () => ({
    //     url: '/user/login-logs',
    //     method: 'GET',
    //   }),
    // }),
    getSessions: build.query<DevicesResponseTypes, void>({
      query: () => ({
        url: '/devices',
        method: 'GET',
      }),
      providesTags: () => ['UserInfo'],
    }),
    endSession: build.mutation<void, string>({
      query: (deviceToken) => ({
        url: `/devices/require-2fa/${deviceToken}`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    endAllSessions: build.mutation<void, void>({
      query: () => ({
        url: '/devices/require-2fa',
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    toggleTwoFA: build.mutation<void, void>({
      query: () => ({
        url: '/devices/update-2fa',
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    generateTelegramToken: build.mutation<TelegramTokenTypes, void>({
      query: () => ({
        url: '/telegram/code',
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    getTelegramToken: build.query<TelegramTokenTypes, void>({
      query: () => ({
        url: '/telegram/code',
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      providesTags: () => ['UserInfo'],
    }),
    resetTelegramToken: build.mutation<void, void>({
      query: () => ({
        url: '/telegram/code',
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    getTelegramSettings: build.query<TelegramSettingsTypes, void>({
      query: () => ({
        url: '/telegram/account',
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      providesTags: () => ['UserInfo'],
    }),
    disconnectTelegramAccount: build.mutation<void, void>({
      query: () => ({
        url: '/telegram/account',
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    setTelegramNotifications: build.mutation<void, TelegramNotificationsBody>({
      query: (body) => ({
        url: '/telegram/account/notifications',
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    toggleFastWithdrawal: build.mutation<void, void>({
      query: () => ({
        url: '/customer/settings/use-fast-withdraw/toggle',
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Profile'],
    }),
  }),
});

export const {
  useGetTabsQuery,
  useGetTabQuery,
  useGetReportsQuery,
  useGetProfileQuery,
  useSignContactMutation,
  useGetInfoUserQuery,
  useLazyGetInfoUserQuery,
  useLazyGetSmsCodeQuery,
  usePromoCodeMutation,
  useChangePasswordMutation,
  // useGetLogsQuery,
  useGetSessionsQuery,
  useEndSessionMutation,
  useEndAllSessionsMutation,
  useGenerateTelegramTokenMutation,
  useGetTelegramTokenQuery,
  useLazyGetTelegramTokenQuery,
  useResetTelegramTokenMutation,
  useGetTelegramSettingsQuery,
  useLazyGetTelegramSettingsQuery,
  useDisconnectTelegramAccountMutation,
  useSetTelegramNotificationsMutation,
  useToggleFastWithdrawalMutation,
  useToggleTwoFAMutation,
} = profileApi;
